import { call } from "redux-saga/effects";
import dayjs from "dayjs";
import { backOfficeApiService } from "../../../../actionService/apiService";
import { ApiCallResult } from "../../../../types";
import DealDto, {
  DealRoutesDto,
  DealAccountDto,
  DealBasicInfoDto,
  DealCargoDto,
  DealCargoPackingListDto,
  DealLiteDto,
  DealSegmentDto,
  DealVehicleDto,
} from "../../../../../dto/Deal/DealDto";
import {
  LoadDealRoutesRequestDto,
  UpdateDealRequestDto,
  GenerateDeliveryRequestDocumentRequestDto,
  GeneratePurchaseRequestDocumentRequestDto,
  GenerateAccountDocumentRequestDto,
  CreateAccountPaymentRequestDto,
  DeleteAccountPaymentRequestDto,
  EditAccountPaymentRequestDto,
  GenerateAccountClosingDocumentsRequestDto,
  DeleteAccountClosingDocumentRequestDto,
  UploadAccountClosingDocumentRequestDto,
  WaitForUpdateRequestDto,
  WaitForRoutesUpdateRequestDto,
  CreateCargoRequestDto,
  CreateDealFromCommercialOfferRequestDto,
  CreateBonusRequestDto,
  ExportAccountClosingDocumentsRequestDto,
  GetAccountClosingDocumentsRequestDto,
  GetBonusesRequestDto,
  GetDealAccountsByIdsRequestDto,
  GetDealRequestDto,
  GetFilterLiteRequestDto,
  ImportCargoPackingListRequestDto,
  SegmentsReplaceAllRequestDto,
  UpdateBonusRequestDto,
  UpdateLiteRequestDto,
} from "./deals.requestsDto";
import endPoints from "./deals.endpoints";
import DeepPartial from "../../../../../types/DeepPartial";
import { EditVehiclesRequestDto } from "../dealVehicle/dealVehicle.requestsDto";
import AccountClosingDocumentDto from "../../../../../dto/Deal/AccountClosingDocumentDto";
import GroupedBonusDto, { BonusDto } from "../../../../../dto/Deal/BonusDto";

const backOfficeDealsApi = {
  loadDealRoutes: function* ({ id, abortController }: LoadDealRoutesRequestDto) {
    return (yield call(
      [backOfficeApiService, backOfficeApiService.Get],
      endPoints.routes(id),
      undefined,
      undefined,
      abortController,
    )) as ApiCallResult<DealRoutesDto>;
  },
  updateDeal: function* ({ deal }: UpdateDealRequestDto) {
    return (yield call([backOfficeApiService, backOfficeApiService.Put], endPoints.controller, deal.id, {
      ...deal,
    })) as ApiCallResult<unknown>;
  },
  generateDeliveryRequestDocument: function* ({ id, values }: GenerateDeliveryRequestDocumentRequestDto) {
    return (yield call([backOfficeApiService, backOfficeApiService.Post], endPoints.document, {
      dealId: id,
      fileFormat: "DOCX",
      documentType: "APPLICATION",
      ...values,
    })) as ApiCallResult<unknown>;
  },
  generatePurchaseRequestDocument: function* ({ id, values }: GeneratePurchaseRequestDocumentRequestDto) {
    return (yield call([backOfficeApiService, backOfficeApiService.Post], endPoints.document, {
      dealId: id,
      fileFormat: "DOCX",
      documentType: "APPLICATION_FOR_CONTRACTOR",
      ...values,
      contractorId: values.contractor.id,
    })) as ApiCallResult<unknown>;
  },
  generateAccountDocument: function* ({ accountId, values }: GenerateAccountDocumentRequestDto) {
    return (yield call([backOfficeApiService, backOfficeApiService.Post], endPoints.accountDocument, {
      accountId,
      fileFormat: "DOCX",
      documentType: "ACCOUNT",
      ...values,
    })) as ApiCallResult<unknown>;
  },
  createAccountPayment: function* ({ payment }: CreateAccountPaymentRequestDto) {
    return (yield call([backOfficeApiService, backOfficeApiService.Post], endPoints.accountPayments, {
      ...payment,
      rateDate: payment.rateDate ? dayjs(payment.rateDate).format("YYYY-MM-DD") : undefined,
    })) as ApiCallResult<unknown>;
  },
  deleteAccountPayment: function* ({ id }: DeleteAccountPaymentRequestDto) {
    return (yield call([backOfficeApiService, backOfficeApiService.Delete], endPoints.accountPayments, id)) as ApiCallResult<unknown>;
  },
  editAccountPayment: function* ({ accountPayment }: EditAccountPaymentRequestDto) {
    return (yield call([backOfficeApiService, backOfficeApiService.Put], endPoints.accountPayments, accountPayment.id, {
      ...accountPayment,
      rateDate: accountPayment.rateDate ? dayjs(accountPayment.rateDate).format("YYYY-MM-DD") : undefined,
    })) as ApiCallResult<unknown>;
  },
  generateAccountClosingDocuments: function* ({ accountId, types }: GenerateAccountClosingDocumentsRequestDto) {
    return (yield call([backOfficeApiService, backOfficeApiService.Post], endPoints.accountClosingDocuments, {
      accountId,
      types,
    })) as ApiCallResult<unknown>;
  },
  deleteAccountClosingDocument: function* ({ ids }: DeleteAccountClosingDocumentRequestDto) {
    return (yield call(
      [backOfficeApiService, backOfficeApiService.Delete],
      endPoints.accountClosingDocuments,
      ids.join(","),
    )) as ApiCallResult<unknown>;
  },
  uploadAccountClosingDocument: function* ({ file, ...rest }: UploadAccountClosingDocumentRequestDto) {
    return (yield call(
      [backOfficeApiService, backOfficeApiService.PostMultipartForm],
      endPoints.accountClosingDocumentsUpload,
      { file: file.originFileObj },
      undefined,
      {
        ...rest,
        date: dayjs(rest.date).format("YYYY-MM-DD"),
      },
    )) as ApiCallResult<unknown>;
  },
  waitForUpdate: function* ({ id, tabId, abortController }: WaitForUpdateRequestDto) {
    return (yield call(
      [backOfficeApiService, backOfficeApiService.Get],
      endPoints.waitForUpdate(id),
      undefined,
      { tabId },
      abortController,
    )) as ApiCallResult<DealDto | { status: 204 }>;
  },
  waitForRoutesUpdate: function* ({ id, updateDate, tabId, abortController }: WaitForRoutesUpdateRequestDto) {
    return (yield call(
      [backOfficeApiService, backOfficeApiService.Get],
      endPoints.waitForRoutesUpdate(id),
      undefined,
      { updateDate, tabId },
      abortController,
    )) as ApiCallResult<DealRoutesDto | { status: 204 }>;
  },
  getById: function* (dto: GetDealRequestDto) {
    return (yield call([backOfficeApiService, backOfficeApiService.Get], endPoints.byId(dto.id))) as ApiCallResult<DealDto>;
  },
  createFromCommercialOffer: function* (dto: CreateDealFromCommercialOfferRequestDto) {
    return (yield call([backOfficeApiService, backOfficeApiService.Post], endPoints.controller, dto)) as ApiCallResult<DealDto>;
  },
  getPendingFill: function* () {
    return (yield call([backOfficeApiService, backOfficeApiService.Get], endPoints.pendingFill)) as ApiCallResult<Array<DealBasicInfoDto>>;
  },
  getDealsLite: function* (dto: GetFilterLiteRequestDto) {
    return (yield call([backOfficeApiService, backOfficeApiService.Get], endPoints.filterLite, undefined, dto)) as ApiCallResult<{
      data: Array<DealLiteDto>;
      total: number;
    }>;
  },
  getAccountClosingDocuments: function* (dto: GetAccountClosingDocumentsRequestDto) {
    return (yield call(
      [backOfficeApiService, backOfficeApiService.Get],
      endPoints.accountClosingDocumentsFilter,
      undefined,
      dto,
    )) as ApiCallResult<{
      data: Array<AccountClosingDocumentDto>;
      total: number;
    }>;
  },
  getAccountsByIds: function* (dto: GetDealAccountsByIdsRequestDto) {
    return (yield call([backOfficeApiService, backOfficeApiService.Get], endPoints.accountsByIds(dto))) as ApiCallResult<
      Array<DealAccountDto>
    >;
  },
  exportAccountClosingDocuments: function* (dto: ExportAccountClosingDocumentsRequestDto) {
    return (yield call([backOfficeApiService, backOfficeApiService.Post], endPoints.accountClosingDocumentsExport, dto)) as ApiCallResult;
  },
  getBonuses: function* (dto: GetBonusesRequestDto) {
    return (yield call([backOfficeApiService, backOfficeApiService.Get], endPoints.bonusesFilter, undefined, dto)) as ApiCallResult<{
      data: Array<GroupedBonusDto>;
      total: number;
    }>;
  },
  createBonus: function* (dto: CreateBonusRequestDto) {
    return (yield call([backOfficeApiService, backOfficeApiService.Post], endPoints.bonuses, dto)) as ApiCallResult<BonusDto>;
  },
  updateBonus: function* (id: number, dto: UpdateBonusRequestDto) {
    return (yield call([backOfficeApiService, backOfficeApiService.Put], endPoints.bonus(id), undefined, dto)) as ApiCallResult<BonusDto>;
  },
  updateDealLite: function* (dto: UpdateLiteRequestDto) {
    return (yield call(
      [backOfficeApiService, backOfficeApiService.Put],
      endPoints.lite(dto.id),
      undefined,
      dto,
    )) as ApiCallResult<DealLiteDto>;
  },
  createCargo: function* (dealId: number, dto: CreateCargoRequestDto) {
    return (yield call(
      [backOfficeApiService, backOfficeApiService.Post],
      endPoints.cargo(dealId),
      dto,
      undefined,
    )) as ApiCallResult<DealCargoDto>;
  },
  importCargoPackingList: function* (dto: ImportCargoPackingListRequestDto) {
    return (yield call([backOfficeApiService, backOfficeApiService.PostMultipartForm], endPoints.importCargoPackingList(dto.dealId), {
      file: dto.file,
    })) as ApiCallResult<DeepPartial<DealCargoPackingListDto>>;
  },
  segmentsReplaceAll: function* (dealId: number, dto: SegmentsReplaceAllRequestDto) {
    return (yield call(
      [backOfficeApiService, backOfficeApiService.Put],
      endPoints.segmentsReplaceAll(dealId),
      undefined,
      dto,
    )) as ApiCallResult<Array<DealSegmentDto>>;
  },
  vehiclesReplaceAll: function* (dealId: number, dto: Array<EditVehiclesRequestDto>) {
    return (yield call(
      [backOfficeApiService, backOfficeApiService.Put],
      endPoints.vehiclesReplaceAll(dealId),
      undefined,
      dto,
    )) as ApiCallResult<Array<DealVehicleDto>>;
  },
};

export default backOfficeDealsApi;
